// [uTheory](http://utheory.com) - Copyright (c) Gregory Ristow 2016
//
// ## Identify multiple notes on a staff
//
// Include description of object & useful info here, please!
import type { ClefName } from 'shared/definitions/musicTheory/clefs';
import { intersection } from 'shared/src/utils/intersection';
import type { ModeAlias } from '../uTheory.Notation/scale';
import Scale from '../uTheory.Notation/scale';
import type { InteractiveSettings } from './interactivePrototype';
import type { SolfegeQuestionObject } from '../uTheory.Skills/solfegeQuestionGenerator';
import SolfegeQuestionGenerator from '../uTheory.Skills/solfegeQuestionGenerator';
import Clef from '../uTheory.Notation/clef';
import Staff from '../uTheory.Notation/staff';
import NoteHolder from '../uTheory.Notation/noteHolder';
import Editable from '../uTheory.Notation/editable';
import Measure from '../uTheory.Notation/measure';
import Voice from '../uTheory.Notation/voice';
import KeySignature from '../uTheory.Notation/keySignature';
import Music from '../uTheory.Notation/music';
import TimeSignature from '../uTheory.Notation/timeSignature';
import type { MultipleNoteSolfegeIDOptions } from './multipleNoteSolfegeID';
import MultipleNoteSolfegeID from './multipleNoteSolfegeID';
import type Note from '../uTheory.Notation/note';
import type { EditorOptions } from '../uTheory.Notation/editor';
import { MultipleNoteSolfegeDictationInteractivePrototype } from './sharedSolfegeIdMethods';

interface Settings extends InteractiveSettings {
  editor: Partial<EditorOptions>;
}

type AnswerStatus = boolean | 'octaveDisplacement' | 'accidental' | 'low' | 'high';

export interface MultipleNoteSolfegeStaffIDOptions extends MultipleNoteSolfegeIDOptions {
  clefs?: ClefName[];
}

export default class MultipleNoteSolfegeStaffID extends MultipleNoteSolfegeDictationInteractivePrototype {
  declare settings: Settings;

  declare options: MultipleNoteSolfegeStaffIDOptions;

  protected currentAccidentalsCount: number;

  staves: {
    // NOT protected so that this can be accessed in tests.
    staff: Staff;
    voice: Voice;
    noteHolders: NoteHolder[];
    clef: Clef;
  }[];

  /** Array of feedback strings that records the feedback for each noteHolder */
  protected feedback: string[];

  /** Array containing which notes in the currentQuestion has been answered correctly (wrong octave counted as correct)  */
  protected priorCorrect: boolean[];

  /** Keeps track of what key the current set of question is in */
  protected currentKey: KeySignature;

  constructor(
    displayDiv: HTMLDivElement,
    options: MultipleNoteSolfegeStaffIDOptions,
    userData?: any
  ) {
    super(displayDiv, options, userData);
    this.setSolfegeSystem();
    this.targetSpeedFromSkills = MultipleNoteSolfegeID.registry[0].skills;
    this.init(displayDiv, options, userData);
  }

  init(displayDiv: HTMLDivElement, options: MultipleNoteSolfegeStaffIDOptions, userData?: any) {
    this.setSolfegeSystem();
    options.maxQuestions = options.maxQuestions || 8;
    this.generator = new SolfegeQuestionGenerator();

    if (options.levelFromUserData) {
      options.level = this.getLevel(userData, 'multiple');
    } else if (options.skills && !options.hasOwnProperty('level')) {
      options.level = this.setLevelFromSkill(options);
    } else if (options.hasOwnProperty('level') && !options.skills) {
      options.skills = [this.getSkillFromLevel(options)];
    } else if (userData && options.drill) {
      options.level = this.getLevel(userData, 'multiple');
    } else {
      throw new Error('options.skills, options.level, or userData must be provided.');
    }

    // Set default notesPerQuestion:
    if (options.drill && !options.notesPerQuestion) {
      options.notesPerQuestion = 4;
    }

    this.settings = {
      loadAudioFirst: !options.testMode, // because we don't want to wait for this to force submit
      instructions: true,
      instructionsPanel: {
        innerHTML:
          '<p>You will hear a chord progression to establish the key,' +
          ' followed by several notes in that key.</p>' +
          '<p>Draw the notes on the staff.</p>',
        callback: this.startGame,
        thisArg: this,
      },
      editor: {
        fixedWidth: 220,
        maxMeasurePadding: 400,
        ariaTitle: () => {
          const ariaString = this.music
            ? this.music.toAriaString({
                rhythm: false,
                barline: false,
                keySignature: { accidentalsCount: true, accidentalsNames: false },
              })
            : '';
          const selected = this.editor && this.editor.getSelected();
          const selectedText =
            selected && selected[0]
              ? selected[0].toAriaString({
                  rhythm: false,
                })
              : '';
          return `${selectedText} ${ariaString}`;
        },
        disableTextInput: true,
      },
      letters: {
        checkButton: true,
        trashButton: true,
        accidentalButtons: options.level >= 5 ? { noDoubleTap: true } : undefined,
        customButtons: [
          { tagName: 'br' },
          {
            text: 'Play T<u>o</u>nic',
            value: 'playTonic',
            hotKey: ['o', 'O'],
            variable: 'playTonic',
          },
          { tagName: 'br', perspective: ['tall', 'portrait', 'square'] },
          { text: '<u>P</u>lay Notes', value: 'play', hotKey: ['p', 'P'], variable: 'playNotes' },
          { tagName: 'br' },
        ],
      },
      checkMark: {},
      player: {},
      progressTracker: true,
    };

    super.init(displayDiv, options, userData);
    this.feedback = [];

    this.loadingPromise.then(() => {
      // which might be called at any point not just on check answer.
      // if a user mouses over or taps on the stave it's redrawn -- so keep track of what
      // feedback text is rendered at any time, and re-render it whenever this
      // is called
      this.editor.renderer.addHook('afterRedraw', () => this.redrawFeedback());
    });

    return this.loadingPromise;
  }

  redrawFeedback() {
    this.currentQuestion.notes.forEach((__, i) => this.updateFeedbackText(i));
  }

  generateQuestions(questions?: string[]): SolfegeQuestionObject[] {
    const solfegeQuestions = super.generateQuestions(questions, true);
    this.questionPrompt = 'Write the notes you hear.';
    if (!this.optionsTrue('testMode')) {
      this.questionPrompt += `<br /><small>#{question} of ${this.questions.length}`;
    }
    return solfegeQuestions;
  }

  startGame() {
    this.gameStarted = true;
    this.displayInstructions();
    this.resize();
  }

  /**
   * Protected method that returns staves and note holders as specified
   */
  protected makeStaves() {
    const level = this.generator.levels.find((l) => l.level === this.options.level);

    if (!level) {
      throw new Error(`Level ${this.options.level} not found.`);
    }

    const notesNumber = this.currentQuestion ? this.currentQuestion.notes.length : 1;
    // At levels 9 and higher, we want to show two staves, one for each clef.
    if (this.options.level >= 9) {
      const staveNames: ClefName[] = ['treble', 'bass'];
      const staves = staveNames.map((clefString) =>
        this.makeStaff(clefString, notesNumber, level.firstNoteGiven)
      );
      staves[0].noteHolders.forEach((nh, i) => {
        const nh2 = staves[1].noteHolders[i];
        nh.editable.link(nh2.editable, { maxNotes: 1, restIfEmptyAndLinkedFull: true });
      });
      return staves;
    }

    // If the clefs options in set, we use the clefs specified in the options.
    if (this.optionSet('clefs')) {
      return this.options.clefs.map((clefString) =>
        this.makeStaff(clefString, notesNumber, level.firstNoteGiven)
      );
    }

    // Interval based levels, that leave the span of the scale's octave, may cross
    // outside of a clef. We will later check at each question initialization and
    // adjust the clef if needed.
    const clefs = this.currentKey.scale.prefersClef();
    const clef = clefs[~~(Math.random() * clefs.length)];
    return [this.makeStaff(clef, notesNumber, level.firstNoteGiven)];
  }

  /**
   *  Sets up the music elements for entering the notes on the staff
   */
  initializeMusic() {
    const keySig = new KeySignature('C', 'Major');
    this.currentKey = keySig;
    this.staves = this.makeStaves();

    const timeSig = new TimeSignature('4/4');
    timeSig.hidden = true;

    const musicElements = this.staves.reduce(
      (arr, staff) => arr.concat([keySig, timeSig, staff.staff, staff.voice]),
      []
    );
    this.music = new Music(musicElements);
  }

  addListeners() {
    function buttonHandler(input) {
      if (input === 'play') {
        this.play();
        return;
      }

      if (input === 'playTonic') {
        this.playTonic();
        return;
      }

      // if (input === 'trash') {
      const noteWasDeleted = this.editor.delete();
      if (!noteWasDeleted) {
        for (let i = this.staves[0].noteHolders.length - 1; i >= 0; i--) {
          let deleted = false;
          this.staves
            .map((staff) => staff.noteHolders[i])
            .forEach((nh) => {
              // A noteholder won't have an editable if the note is given.
              const deleteable = !!nh.editable;
              if (nh.length && deleteable) {
                deleted = true;
                nh.length = 0;
                this.editor.renderer.render();
              }
            });
          if (deleted) break;
        }
        // }
      }
    }

    this.letterPanel.addCustomButtonCallback(buttonHandler, this);
    this.letterPanel.addAccidentalCallback(this.editor.setAccidental, this.editor);
    this.letterPanel.addCheckCallback(this.checkAnswer, this);
  }

  /**
   * Clear the noteHolder, re-render the notation
   * Set the key signature for the key of the music being played and generate new question
   */
  newQuestion(disableAudio?: any) {
    super.newQuestion(disableAudio);

    this.initializeNewQuestionMusic();

    // Initialize priorCorrect to all wrong
    this.priorCorrect = this.currentQuestion.notes.map(() => false);

    const notesCount = this.currentQuestion.notes.length;
    const width = this.currentAccidentalsCount * 10 + notesCount * 60;
    this.editor.options.fixedWidth = width;
    this.editor.renderer.options.fixedWidth = width;

    this.editor.renderer.render();
  }

  /**
   * Detects number of notes and key signature of new question and initialize noteHolders, music, etc accordingly
   */
  protected initializeNewQuestionMusic() {
    if (this.currentQuestion.key) {
      this.currentAccidentalsCount = Math.abs(this.currentQuestion.key.accidentalsCount);
      this.currentKey = this.currentQuestion.key;
    }

    this.staves = this.makeStaves();

    // If this isn't a grand staff level, be sure we're using a clef that
    // works for all of the notes!
    if (this.options.level! < 9) {
      // What clefs will work?
      const possibleClefs: ClefName[] = this.currentQuestion.notes!.reduce((clefs, note, index) => {
        const noteClefs = note.prefersClef(this.options.clefs || ['treble', 'bass']);
        // If this isn't the first note, we only want clefs that are in the intersection
        // Otherwise we want all clefs that work for this note.
        return index ? intersection(clefs, noteClefs) : noteClefs;
      }, [] as ClefName[]);

      // If we don't have any clefs that work, we throw an error.
      if (!possibleClefs.length) {
        throw new Error('No clefs work for this question!');
      }

      // If we have more than one clef that works, we pick one at random.
      const clef = possibleClefs[~~(Math.random() * possibleClefs.length)];

      // Now we set the clef for each staff.
      this.staves.forEach((staff) => {
        staff.clef = new Clef(clef);
      });
    }

    // Sets key for each noteHolder in each staff
    this.staves.forEach((staff) =>
      staff.noteHolders.forEach((noteHolder) => {
        if (noteHolder.editable) {
          noteHolder.editable.setKeySignature(this.currentKey);
        }
      })
    );

    const musicElements = this.staves.reduce(
      (arr, staff) => arr.concat([this.currentKey, staff.staff, staff.voice]),
      []
    );

    this.music = new Music(musicElements);
    this.editor.renderer.music = this.music;
  }

  /**
   * Get Notes from different staves in the same verticality into one Note, while
   * maintaining the original Notes
   */
  protected dissolveNotes() {
    const notesNumber = this.currentQuestion.notes.length;
    const notes: Note[] = [];
    // index i keeps track of the current verticality. e.g. beat 1, i == 0; beat 2, i == 1
    for (let i = 0; i < notesNumber; i++) {
      let note: Note;
      this.staves.forEach((staff) => {
        // More than one note in same verticality same staff
        if (staff.noteHolders[i].notes.length > 1) {
          throw Error('More than one note per verticality');
        }
        // More than one note in same verticality different staff
        if (note && !staff.noteHolders[i].isEmpty()) {
          throw Error('More than one note per verticality');
        }
        // One note per verticality
        if (!staff.noteHolders[i].isEmpty()) {
          note = staff.noteHolders[i].notes[0];
        }
      });
      notes.push(note);
    }
    return notes;
  }

  /**
   * Returns an array with the status of each note:
   * true: correct
   * false: incorrect with no clues (before 2 attempts have been used)
   * 'octaveDisplacement': octave displacement (given even in the first attempt)
   * 'low': too low
   * 'high': too high
   * 'accidental': wrong accidental
   * --------------------------------
   * this.answerIsRelative is set to true if too many notes (>75%) are in the relative key
   */
  protected processAnswer(answer: Note[]) {
    const status: AnswerStatus[] = [];
    let rightAnswers = 0;
    /** Number of notes that might have been mistaken to be in the relative key. */
    let numNotesRelative = 0;
    const relativeKey = this.currentKey.getRelativeKey();

    // Evaluate each note
    for (let i = 0; i < this.currentQuestion.notes.length; i++) {
      const answerNote = answer[i];
      // In testMode, it is possible for a note to be missing and the answer still be processed
      if (answerNote === undefined) {
        status.push(false);
        continue;
      }
      const correctNote = this.currentQuestion.notes[i];

      const correctIgnoreOctave = answerNote.equalsIgnoreOctave(correctNote);
      // if note name and accidental is correct, record as correct answer
      if (correctIgnoreOctave && !this.priorCorrect[i]) {
        this.priorCorrect[i] = true;
        rightAnswers++;
      }
      const octaveDisplacement = correctIgnoreOctave && !answerNote.equals(correctNote);

      if (octaveDisplacement) {
        status.push('octaveDisplacement');
        continue;
      }

      if (correctIgnoreOctave) {
        status.push(true);
        continue;
      }

      if (this.wrongAnswerCount === 0) {
        status.push(false);
        continue;
      }

      const relativeNote = relativeKey.getNoteAtScaleDegreeOrThrow(this.currentQuestion.degrees[i]);
      if (answerNote.letter === relativeNote.letter) numNotesRelative++;

      if (answerNote.letter === correctNote.letter) {
        status.push('accidental');
        continue;
      }

      if (answerNote.isLessThan(correctNote)) {
        status.push('low');
        continue;
      }

      status.push('high');
    }

    // Assume answer is not relative
    let answerIsRelative = false;
    // Only check for relative-key-mistake if this is the first question of key and the user
    // already got one wrong.
    if (this.currentQuestion.key && this.wrongAnswerCount > 0) {
      // percentRelative is the fraction of the notes that might have been mistaken to be in
      // the relative key
      const percentRelative = numNotesRelative / this.currentQuestion.notes.length;
      // 75% is the threshold to say that the user might have mistaken the key for the its relative key
      if (percentRelative >= 0.75) answerIsRelative = true;
    }

    const wrongAnswers = this.priorCorrect.reduce((total, correct) => total + (correct ? 0 : 1), 0);
    return { rightAnswers, wrongAnswers, answerIsRelative, answerStatusArr: status };
  }

  checkAnswer() {
    if (this.rightAnswerDelay) return;
    // Checks no NoteHolders are ghosting
    const someNotesUnanswered = this.currentQuestion.notes.find((q, i) => {
      const isEmpty = this.staves.every((staff) => staff.noteHolders[i].length === 0);
      return isEmpty;
    });

    if (someNotesUnanswered && !this.options.testMode) {
      this.checkMark.warn('There are some notes missing');
      return;
    }

    let answer: Note[];
    try {
      answer = this.dissolveNotes();
    } catch (e) {
      console.error(e);
      this.checkMark.warn('There is more than one note per beat');
      return;
    }

    this.feedback = [];
    const { rightAnswers, wrongAnswers, answerStatusArr, answerIsRelative } =
      this.processAnswer(answer);

    const correct = answerStatusArr.every((status) => status === true);
    // Determines if any note is correct but off by octave
    answerStatusArr.forEach((status, i) => {
      if (status === 'octaveDisplacement') {
        this.updateFeedbackText(i, 'check 8ve');
      }
      this.editor.renderer.render();
    }, this);

    // For test mode and non-test, as long as there is one wrong octave, player gets to try til octave is correct.
    if (wrongOctaveOnly(answerStatusArr)) {
      if (!this.options.testMode) this.trackAnswer(rightAnswers, wrongAnswers);
      this.colorWrongNotes();
      return;
    }

    // If test mode, submit answer without giving right/wrong feedback unless octave-mistake only (above)

    if (this.options.testMode) {
      this.checkpointAnswer({ answerNotes: answer });
      return;
    }

    // For non-test mode
    // If correct, right
    if (correct) {
      this.trackAnswer(rightAnswers, wrongAnswers);
      this.rightAnswer(250);
      return;
    }

    // Annotate each notes for the mistake that they currently have
    answerStatusArr.forEach((status, i) => {
      switch (status) {
        case 'accidental':
          this.updateFeedbackText(i, 'wrong accidental');
          break;
        case 'low':
          this.updateFeedbackText(i, 'too low');
          break;
        case 'high':
          this.updateFeedbackText(i, 'too high');
          break;
      }
    });

    // If the player were possibly thinking of the relative key, let them know
    if (answerIsRelative) {
      this.wrongAnswer(
        `Were you thinking of ${this.currentKey.getRelativeKey()}
        instead of ${this.currentKey}?`,
        rightAnswers,
        wrongAnswers
      );
      return;
    }

    this.wrongAnswer(undefined, rightAnswers, wrongAnswers);
  }

  getScaleAndFirstWrongDegree(): { scale?: Scale; degree: string | false } {
    const keyQuality = this.key.quality;
    let quality: string = keyQuality;

    const { answerStatusArr } = this.processAnswer(this.dissolveNotes());
    const wrongAnswerIndex = answerStatusArr.findIndex(
      (status) => status !== true && status !== 'octaveDisplacement'
    );

    if (wrongAnswerIndex === -1) return { degree: false };

    let degree = this.currentQuestion.degrees[wrongAnswerIndex];

    if (degree === 'M6') {
      quality = 'melodic minor';
      degree = '6';
    }
    if (degree === 'M7') {
      quality = 'harmonic minor';
      degree = '7';
    }

    const tonic = this.key.tonic.clone();
    const questionNote = this.currentQuestion.notes[wrongAnswerIndex];
    tonic.octave = questionNote.octave;
    if (tonic.isGreaterThan(questionNote)) tonic.octave--;

    const scale = new Scale(tonic, quality as ModeAlias);
    return { scale, degree };
  }

  /**
   * Color the all wrong notes red
   */
  protected colorWrongNotes() {
    if (this.wrongAnswerCount < 1) return;

    for (let i = 0; i < this.currentQuestion.notes.length; i++) {
      const questionNote = this.currentQuestion.notes[i];
      this.staves.forEach((staff) => {
        const nh = staff.noteHolders[i];
        if (!nh.isEmpty()) {
          const answerNote = nh.notes[0];
          const correct = answerNote.equalsIgnoreOctave(questionNote);
          if (correct) delete nh.color;
          else nh.color = 'red';
        }
      });
    }
    this.editor.renderer.render();
  }

  wrongAnswer(warningText = '', rightAnswers?: number, wrongAnswers?: number) {
    if (warningText) this.checkMark.xAndWarn(warningText);
    else this.checkMark.x();
    this.trackAnswer(rightAnswers, wrongAnswers);
    this.colorWrongNotes();
    if (++this.wrongAnswerCount > 1) {
      this.playHint();
    }
  }

  override rightAnswer(delay?: number) {
    this.checkMark.check();
    this.colorWrongNotes();

    const gameOver = this.gameIsOver(true);

    this.questionNumber++;

    this.rightAnswerCallbacks.forEach((callback) => {
      callback(gameOver);
    }, this);

    // If the game's not over, go on to the next question.
    if (delay) {
      this.rightAnswerDelay = true;
      window.setTimeout(() => {
        if (gameOver) this.gameOver();
        else {
          this.newQuestion();
        }
      }, delay);
    } else if (gameOver) this.gameOver();
    else this.newQuestion();
  }

  /**
   * Updates the progress tracker with number of correct and incorrect answers
   */
  protected trackAnswer(rightAnswers: number, wrongAnswers: number) {
    if (!rightAnswers && !wrongAnswers) return;
    const trackerObject = {
      skill: this.currentQuestion.skills,
      questions: rightAnswers + wrongAnswers,
      right: rightAnswers,
      wrong: wrongAnswers,
    };
    this.progressTracker.multipleAnswers([trackerObject], rightAnswers + wrongAnswers, false);
  }

  /**
   * Adds feedback annotation to a wrong noteHolder. If no text is supplied, go with the current feedback
   */
  protected updateFeedbackText(
    noteHolderIndex: number,
    text = this.feedback[noteHolderIndex] || ''
  ) {
    // for each text feedback, do this:
    let staff: {
      staff: Staff;
      voice: Voice;
      noteHolders: NoteHolder[];
      clef: Clef;
    };

    if (this.staves[1] && !this.staves[1].noteHolders[noteHolderIndex].isEmpty()) {
      staff = this.staves[1];
    } else staff = this.staves[0];

    const noteHolder = staff.noteHolders[noteHolderIndex];
    if (noteHolder.length === 0) return;

    const { x, w, y, h } = noteHolder.staveNote.getBoundingBox();
    const centerX = x + w / 2;
    const bottom = y + h;
    const line2 = noteHolder.staveNote.getStave().getYForLine(2);
    const above = bottom - line2 > line2 - y;
    const textY = above
      ? Math.min(noteHolder.staveNote.getStave().getYForLine(-1), y - 10)
      : Math.max(noteHolder.staveNote.getStave().getYForLine(5), bottom + 10);
    const { ctx } = this.editor.renderer;
    ctx.save();
    ctx.attributes['text-anchor'] = 'middle';
    ctx.attributes.fill = 'red';
    this.feedback[noteHolderIndex] = text;

    this.editor.renderer.ctx.fillText(text, centerX, textY);
    ctx.restore();
  }

  /**
   * Make the staff/staves and noteHolders for each new question
   */
  protected makeStaff(clefName: ClefName, notesNumber: number, firstNoteGiven: boolean = false) {
    const clef = new Clef(clefName);
    const staff = new Staff(clef);
    const measure = new Measure();
    const noteHolders: NoteHolder[] = [];

    for (let i = 0; i < notesNumber; i++) {
      const noteHolder = new NoteHolder('', 'w');

      // If the first note is given, make it uneditable and add it to the measure
      if (this.currentQuestion && firstNoteGiven && i === 0) {
        const note = this.currentQuestion.notes![0]!;
        noteHolder.notes = [note];
      } else {
        noteHolder.editable = new Editable(noteHolder, {
          pitch: true,
          accidental: false,
          deleteable: ['placeHolderRest'],
          selectable: true,
        });
        const clearFeedback = () => {
          noteHolder.color = undefined;
          this.updateFeedbackText(i, '');
        };
        noteHolder.editable.eventHandler.addListener('pitchChange', clearFeedback);
        noteHolder.editable.eventHandler.addListener('accidentalChange', clearFeedback);
      }

      measure.push(noteHolder);
      noteHolders.push(noteHolder);
    }
    const voice = new Voice([measure]);
    voice.setStaff(staff);
    return {
      staff,
      voice,
      clef,
      noteHolders,
    };
  }

  getAnswerAndData(answerObj: { answerNotes?: Note[]; correct: boolean } = { correct: false }) {
    const rightAnswers = this.priorCorrect.reduce((total, correct) => total + (correct ? 1 : 0), 0);
    const wrongAnswers = this.priorCorrect.reduce((total, correct) => total + (correct ? 0 : 1), 0);

    const studentAnswerText = this.currentQuestion.notes
      .map((note, index) => {
        if (!answerObj.answerNotes || !answerObj.answerNotes[index]) return '___';
        return answerObj.answerNotes[index].getName();
      })
      .join(' ');

    const question = this.currentQuestion.notes.reduce((total, note) => {
      total += `${note.getName()} `;
      return total;
    }, '');

    return {
      question,
      rightAnswers,
      wrongAnswers,
      studentAnswerText,
      questionType: 'MultipleNoteSolfegeStaffID',
      questionText: this.instructionsDiv.innerHTML,
      questionSVG: null,
      studentAnswerSVG: this.editor.renderer.exportSVG(),
      correctAnswerText: question,
      skills: [this.currentQuestion.skills],
    };
  }

  static registry = {
    description: '4-note dictation',
    options: {
      levelFromUserData: true,
      tonicizeEvery: 4,
    },
    skills: [
      'sd_frag_123_major',
      'sd_frag_123_minor',
      'sd_frag_12345_major',
      'sd_frag_12345_minor',
      'sd_frag_5678_major',
      'sd_frag_5678_minor',
      'sd_frag_5678_all_minors',
      'sd_frag_1_to_8_major',
      'sd_frag_1_to_8_minor',
      'sd_frag_1_to_8_major_w_octaves',
      'sd_frag_1_to_8_minor_w_octaves',
    ],
  };
}

/**
 * Returns whether there are only octave mistakes in the answer
 */
export function wrongOctaveOnly(statusArr: AnswerStatus[]) {
  // Maybe they're all correct:
  if (statusArr.every((status) => status === true)) return false;

  // Or maybe there's one that's in the wrong octave:
  return statusArr.every((status) => status === true || status === 'octaveDisplacement');
}
